import React, { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';

import {
  getOrderSummary,
  applyDiscountCode,
  removeDiscountCode,
  getCartItems,
  createCheckoutOrder,
} from '../../CheckoutService';

const OrderSummary = (): JSX.Element => {
  const theme = useTheme();
  const [cartItems, setCartItems] = useState<any[]>([]);
  const [cartSummary, setOrderSummary] = useState({
    cartId: null,
    subtotal: 0,
    discountCode: '',
    discountAmount: 0,
    vatAmount: 0,
    total: 0,
  });
  const [discountCode, setDiscountCode] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCartItems();
    fetchOrderSummary();
  }, []);

  const fetchCartItems = () => {
    setLoading(true);
    getCartItems()
      .then((response) => {
        setCartItems(response.data);
      })
      .catch((error) => console.error(error))
      .finally(() => setLoading(false));
  };

  const fetchOrderSummary = () => {
    setLoading(true);
    getOrderSummary()
      .then((response) => {
        setOrderSummary(response.data);
        setDiscountCode(response.data.discountCode || '');
      })
      .catch((error) => console.error(error))
      .finally(() => setLoading(false));
  };

  const validateDiscountCode = (code: string): boolean => {
    // Add validation logic (e.g., regex or API-based validation)
    return code.trim().length > 0; // Example: code must not be empty
  };

  const handleApplyDiscountCode = () => {
    if (!validateDiscountCode(discountCode)) {
      alert('Discount Code Invalid');
      return;
    }

    setLoading(true);
    applyDiscountCode(discountCode)
      .then(() => {
        alert('Discount code applied successfully!');
        fetchOrderSummary(); // Refresh the cart summary
      })
      .catch((error) => {
        console.error('Error applying discount code:', error);

        // Check if error indicates invalid discount code
        if (error.response?.status === 400) {
          alert('Discount Code Invalid');
        }
        // Handle session expiry
        else if (
          error.response?.status === 401 ||
          error.response?.status === 403
        ) {
          alert('Session has expired. Please log in again.');
          window.location.href = '/login';
        }
        // General error fallback
        else {
          alert('Discount code invalid. Please check code and try again.');
        }
      })
      .finally(() => setLoading(false));
  };

  const handleRemoveDiscountCode = () => {
    setLoading(true);
    removeDiscountCode()
      .then(() => {
        alert('Discount code removed successfully!');
        fetchOrderSummary(); // Refresh the cart summary
      })
      .catch((error) => {
        console.error('Error removing discount code:', error);
        alert('Failed to remove discount code. Please try again later.');
      })
      .finally(() => setLoading(false));
  };

  return (
    <Box>
      <Box>
        {loading && <p>Loading...</p>}

        {cartItems.length === 0 && !loading && (
          <Typography
            variant="h6"
            color="text.secondary"
            align="center"
            sx={{ marginTop: 3 }}
          >
            Your cart is empty. Start adding items to your cart!
          </Typography>
        )}

        {cartItems.map((item, i) => (
          <Box key={i}>
            <Box display={'flex'}>
              <Box
                component={'img'}
                // src={item.image}
                src={'/assets/favicon.ico'}
                alt={item.title}
                sx={{
                  borderRadius: 2,
                  width: 1,
                  height: 1,
                  maxWidth: 120,
                  marginRight: 2,
                  filter:
                    theme.palette.mode === 'dark' ? 'brightness(0.7)' : 'none',
                }}
              />
              <Box
                display={'flex'}
                flexDirection={{ xs: 'column', sm: 'row' }}
                justifyContent={'space-between'}
                alignItems={'flex-start'}
                width={1}
              >
                <Box>
                  <Typography
                    fontWeight={700}
                    variant={'subtitle2'}
                    sx={{ marginBottom: 1 }}
                  >
                    {item.product.productName}
                  </Typography>
                  <Typography color={'text.secondary'} variant={'subtitle2'}>
                    Type: <strong>{item.product.productType}</strong>
                  </Typography>
                  <Typography color={'text.secondary'} variant={'subtitle2'}>
                    Duration: <strong>{item.product.costFrequencyUnit}</strong>
                  </Typography>
                  <Typography
                    color={'text.secondary'}
                    variant={'subtitle2'}
                    noWrap={true}
                  >
                    Code: <strong>{item.product.apps[0].appCode}</strong> (
                    <strong>{item.product.apps[0].appName}</strong>)
                  </Typography>
                </Box>
                <Box>
                  <Typography fontWeight={700} variant={'subtitle2'}>
                    {item.product.productCost === 0
                      ? 'Free'
                      : `£${item.product.productCost.toFixed(2)}`}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Divider
              sx={{
                marginY: { xs: 2, sm: 4 },
                display: i === cartItems.length - 1 ? 'none' : 'block',
              }}
            />
          </Box>
        ))}
      </Box>
      <Box
        component={'form'}
        noValidate
        autoComplete="off"
        sx={{
          marginY: 4,
          '& .MuiInputBase-input.MuiOutlinedInput-input': {
            bgcolor: 'background.paper',
          },
        }}
      >
        <Box>
          <TextField
            label="Discount code"
            variant="outlined"
            color="primary"
            fullWidth
            value={discountCode}
            onChange={(e) => setDiscountCode(e.target.value)}
            disabled={!!cartSummary.discountCode || loading}
          />
          {cartSummary.discountCode ? (
            <Button
              color="secondary"
              size="large"
              fullWidth
              sx={{
                bgcolor: 'divider',
                color: 'text.primary',
                marginTop: 1,
              }}
              onClick={handleRemoveDiscountCode}
              disabled={loading}
            >
              Remove code
            </Button>
          ) : (
            <Button
              color="primary"
              size="large"
              fullWidth
              sx={{
                bgcolor: 'divider',
                color: 'text.primary',
                marginTop: 1,
              }}
              onClick={handleApplyDiscountCode}
              disabled={loading}
            >
              Apply this code
            </Button>
          )}
        </Box>
      </Box>
      <Stack spacing={2} marginY={{ xs: 2, sm: 4 }}>
        <Box display={'flex'} justifyContent={'space-between'}>
          <Typography color={'text.secondary'}>Subtotal</Typography>
          <Typography color={'text.secondary'} fontWeight={700}>
            £{cartSummary.subtotal.toFixed(2)}
          </Typography>
        </Box>
        <Box display={'flex'} justifyContent={'space-between'}>
          <Typography color={'text.secondary'}>Discount</Typography>
          <Typography color={'text.secondary'} fontWeight={700}>
            -£{cartSummary.discountAmount.toFixed(2)}
          </Typography>
        </Box>
        <Box display={'flex'} justifyContent={'space-between'}>
          <Typography color={'text.secondary'}>VAT (+20%)</Typography>
          <Typography color={'text.secondary'} fontWeight={700}>
            £{cartSummary.vatAmount.toFixed(2)}
          </Typography>
        </Box>
        <Divider />
        <Box display={'flex'} justifyContent={'space-between'}>
          <Typography variant={'h6'} fontWeight={700}>
            Order total
          </Typography>
          <Typography variant={'h6'} fontWeight={700}>
            £{cartSummary.total.toFixed(2)}
          </Typography>
        </Box>
        <Button
          variant={'contained'}
          size={'large'}
          fullWidth
          onClick={() => {
            setLoading(true); // Optional: Indicate loading state
            createCheckoutOrder()
              .then(() => {
                alert('Order placed successfully!');
                window.location.href = '/ecommerce/order-complete'; // Redirect to order-complete page
              })
              .catch((error) => {
                console.error('Error placing order:', error);
                alert('Failed to place the order. Please try again.');
              })
              .finally(() => setLoading(false)); // Reset loading state
          }}
        >
          Place an order
        </Button>
      </Stack>
    </Box>
  );
};

export default OrderSummary;
